import { Layout } from '@community_dev/pixels'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Spin } from 'antd'
import type { JSX } from 'react'

import { SettingsPage } from '../Settings'

import { MasterPhoneNumber } from './MasterPhoneNumber'
import { Overview } from './Overview'
import { PhoneNumbers } from './PhoneNumbers'
import { TCRCampaigns } from './TCRCampaigns'

import { Client } from 'api/clients'
import { getProvisioningStatus, ProvisioningStatus } from 'api/infobipProvisioning'
import { getVoiceStatus, VoiceStatus } from 'api/infobipVoice'
import { CAPABILITIES } from 'constants/capabilities'
import { QUERIES } from 'constants/queries'
import { useHasCapability } from 'hooks/useCapabilities'

type NumberProvisioningProps = {
  client: Client
}

export const NumberProvisioning = ({ client }: NumberProvisioningProps): JSX.Element => {
  const canWrite = useHasCapability(CAPABILITIES.ADMIN.NUMBER_PROVISIONING.WRITE)
  const disabledTooltip = 'You do not have the capability to manage number provisioning'
  const {
    data: provisioningStatus,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<ProvisioningStatus>([QUERIES.PROVISIONING_STATUS, client.id], () =>
    getProvisioningStatus({ clientId: client.id }),
  )

  const hasNumbers = (provisioningStatus?.numbers.length ?? 0) > 0

  const { data: voiceStatus, isInitialLoading: isVoiceLoading } = useQuery<VoiceStatus>(
    [QUERIES.VOICE_STATUS, client.id],
    () => getVoiceStatus({ clientId: client.id }),
    { enabled: hasNumbers },
  )

  // voice_setups is keyed by phone number without the leading "+" that masterPhoneNumber carries.
  const masterVoiceLookupKey = client.masterPhoneNumber?.replace(/^\+/, '')

  const isMasterVoiceSetup = voiceStatus
    ? Boolean(masterVoiceLookupKey && voiceStatus.voiceSetups[masterVoiceLookupKey]?.action)
    : undefined

  if (isLoading || isVoiceLoading) {
    return (
      <Layout alignItems="center" display="flex" justifyContent="center" padding="24px">
        <Spin size="large" />
      </Layout>
    )
  }

  if (isError) {
    return (
      <Layout padding="24px">
        <Alert
          action={
            <Button onClick={() => refetch()} size="small" type="primary">
              Retry
            </Button>
          }
          description="Could not load provisioning status. This might be a new client without any provisioning data yet."
          message="Error Loading Provisioning Status"
          showIcon
          type="error"
        />
      </Layout>
    )
  }

  return (
    <SettingsPage
      description="Phone numbers and TCR campaigns provisioned for this client."
      title="Number Provisioning"
    >
      <MasterPhoneNumber
        currentMasterNumber={client.masterPhoneNumber}
        isMasterVoiceSetup={isMasterVoiceSetup}
        provisioningStatus={provisioningStatus?.status}
      />
      <Overview isFetching={isFetching} onRefresh={() => refetch()} provisioningStatus={provisioningStatus} />
      <PhoneNumbers
        campaigns={provisioningStatus?.campaigns ?? []}
        canWrite={canWrite}
        clientId={client.id}
        currentMasterNumber={client.masterPhoneNumber}
        disabledTooltip={disabledTooltip}
        numbers={provisioningStatus?.numbers ?? []}
        voiceOptions={voiceStatus?.options}
        voiceSetups={voiceStatus?.voiceSetups}
      />
      <TCRCampaigns
        campaigns={provisioningStatus?.campaigns ?? []}
        canWrite={canWrite}
        clientId={client.id}
        disabledTooltip={disabledTooltip}
      />
    </SettingsPage>
  )
}
